import { CircleAlert, CircleCheck, FlaskConical } from 'lucide-react';
import type { ReactNode } from 'react';
import type {
  Branch,
  Draft,
  InvoiceKind,
  MockScenario,
  OrgActivityCode,
  Organization,
  OrganizationTax,
  Party,
  PreviewResult,
} from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { Callout } from '../../../ui/Feedback';
import { Money } from '../../../ui/Layout';
import { SelectField } from '../../../ui/Select';
import { formatAddress } from '../../address';
import type { NoteSource } from '../../../api/types';
import styles from '../Sales.module.css';
import type { LineInfo } from './LineEditor';
import type { ReferenceLineInfo } from './noteReferences';
import type { StepId } from './steps';

/** The step-order sections the review can hold (step 1 is the buyer, or a note's reference). */
export type ReviewSectionId = 'buyer' | 'reference' | 'details' | 'lines';

/** The default, invoice order; a note passes `order={['reference', 'details', 'lines']}`. */
const DEFAULT_ORDER = ['buyer', 'details', 'lines'] as const satisfies readonly ReviewSectionId[];

const SCENARIOS = [
  'valid',
  'invalid',
  'not_accepted',
  'rejected',
  'cancelled',
  'retry_wait',
  'unknown',
] as const;

/**
 * Step 4, «المراجعة والإصدار» (BLOCK-1-REDESIGN §A.2; role 4 RD4-03): a check in step order, not a
 * rendering of the document. One section per step (buyer, document details, invoice lines), each
 * with its open issues and «عُد إلى …»; the totals stay in the rail. Then, for a user who can issue,
 * the buyer's e-mail copy «نسخة للمشتري بالبريد» (WP-1b.4, the optional `mail` slot), and for mock
 * organizations the «للاختبار فقط» box with the mock answer (DV-9).
 */
export function ReviewStep({
  counts,
  titles,
  onGoTo,
  sections,
  order = DEFAULT_ORDER,
  isMock,
  scenario,
  onScenario,
  mail,
  permissionNotice,
}: {
  counts: Record<StepId, number>;
  titles: Record<StepId, string>;
  onGoTo: (step: StepId) => void;
  /**
   * What each section shows: `ReviewBuyerSection`/`ReviewReferenceSection`, `ReviewDetailsSection`,
   * `ReviewLinesSection`.
   */
  sections: Partial<Record<ReviewSectionId, ReactNode>>;
  /** The step order, step 4 excluded. Defaults to the invoice order; a note leads with the reference. */
  order?: readonly ReviewSectionId[];
  isMock: boolean;
  scenario: MockScenario;
  onScenario: (scenario: MockScenario) => void;
  /** WP-1b.4: the buyer-copy block (`MailChoice`), after the checks and before the mock box. */
  mail?: ReactNode;
  /**
   * Block 11 (D-22): the check that explains why issuing is withheld — e.g. a credit note needs
   * `invoice.cancel`. Shown above the sections; the step bar then offers no issue action.
   */
  permissionNotice?: ReactNode;
}) {
  const { t } = useI18n();
  const open = order.reduce((n, id) => n + counts[id], 0) + counts.review;
  return (
    <div className={styles.stepBody}>
      <p className={styles.reviewLead}>
        {open ? (
          <CircleAlert size={16} aria-hidden="true" />
        ) : (
          <CircleCheck size={16} aria-hidden="true" />
        )}
        <span>{open ? t.editor.review.title : t.editor.review.clean}</span>
      </p>
      {permissionNotice ? (
        <Callout tone="warning" role="note">
          <span data-testid="review-permission-note">{permissionNotice}</span>
        </Callout>
      ) : null}
      {order.map((id) => (
        <section
          key={id}
          className={styles.reviewSection}
          aria-labelledby={`review-${id}-title`}
          data-testid={`review-section-${id}`}
          data-issues={counts[id]}
        >
          <div className={styles.reviewHead}>
            <h3 id={`review-${id}-title`} className={styles.reviewTitle}>
              {titles[id]}
            </h3>
            <span
              className={`${styles.reviewCount} ${counts[id] ? styles.countOpen : styles.countClean}`}
            >
              {counts[id] ? t.editor.readiness.incomplete(counts[id]) : t.editor.steps.clean}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className={styles.textButton}
              onPress={() => onGoTo(id)}
              testId={`review-back-${id}`}
            >
              {t.editor.review.backTo(titles[id])}
            </Button>
          </div>
          {sections[id] ?? null}
        </section>
      ))}
      {mail ?? null}
      {isMock ? (
        <section className={styles.testingBox} aria-labelledby="review-testing-title">
          <h3 id="review-testing-title" className={styles.reviewTitle}>
            <FlaskConical size={16} aria-hidden="true" /> {t.editor.review.testingOnly}
          </h3>
          <SelectField
            label={t.mock.scenario}
            description={t.mock.scenarioHint}
            fieldName="mockScenario"
            value={scenario}
            onChange={(v) => v && onScenario(v as MockScenario)}
            options={SCENARIOS.map((s) => ({ id: s, label: t.mock.scenarios[s] }))}
          />
        </section>
      ) : null}
    </div>
  );
}

/** One definition list of the review: a term and its value per row (BR-3). */
function Pairs({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className={styles.reviewList}>
      {rows.map(([term, value]) => (
        <div key={term} className={styles.reviewPair}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The chosen buyer as the review shows it (the party record with its addresses). */
export type ReviewParty = Party & {
  addresses?: {
    country: string;
    governate: string;
    regionCity: string;
    street: string;
    buildingNumber: string;
    isDefault?: boolean;
  }[];
};

/** «المشتري»: where it is sold, then the buyer's name and address, category and ID. */
export function ReviewBuyerSection({
  draft,
  kind,
  party,
}: {
  draft: Draft;
  kind: InvoiceKind;
  party: ReviewParty | null;
}) {
  const { t } = useI18n();
  const rows: [string, ReactNode][] = [
    [t.editor.settings.type, kind === 'ei' ? t.editor.settings.export : t.editor.settings.local],
  ];
  if (!draft.receiver) rows.push([t.editor.customer.search, t.sales.noCustomer]);
  else if ('cash' in draft.receiver) rows.push([t.editor.customer.search, t.editor.customer.cash]);
  else if (party) {
    const address = party.addresses?.find((a) => a.isDefault) ?? party.addresses?.[0];
    const idLabel =
      party.partyType === 'P'
        ? t.parties.taxIdP
        : party.partyType === 'F'
          ? t.parties.taxIdF
          : t.parties.taxIdB;
    rows.push([
      t.parties.name,
      <>
        <span className={styles.reviewStrong}>{party.name}</span>
        {address ? <span className={styles.reviewSub}>{formatAddress(address)}</span> : null}
      </>,
    ]);
    rows.push([t.parties.type, t.parties.types[party.partyType]]);
    rows.push([idLabel, party.taxId ? <span className={styles.mono}>{party.taxId}</span> : '—']);
  } else rows.push([t.editor.customer.search, '…']);
  return <Pairs rows={rows} />;
}

/**
 * Block 11 step 1 in the review, «المستند المرجعي»: the buyer taken from the reference (read only)
 * and the chosen references, each in AC-N7 order — number · total · «المتبقي القابل للتخفيض»
 * (credit notes) · date · Tax Authority identifier. The reference is chosen only in step 1 of the
 * main area (AC-N2); here it is shown, never chosen.
 */
export function ReviewReferenceSection({
  ids,
  picked,
}: {
  ids: readonly string[];
  picked: Record<string, NoteSource>;
}) {
  const { t, formatDate, formatDateTime } = useI18n();
  const refs = ids.map((id) => picked[id]).filter((r): r is NoteSource => Boolean(r));
  const buyerName = refs.find((r) => r.receiverName)?.receiverName ?? null;
  const rows: [string, ReactNode][] = [
    [
      t.parties.name,
      buyerName ? (
        <>
          <span className={styles.reviewStrong}>{buyerName}</span>
          <span className={styles.reviewSub}>{t.editor.note.fromReferenceField}</span>
        </>
      ) : (
        '…'
      ),
    ],
  ];
  for (const r of refs) {
    // M-1: the document date (the local Cairo date), never a raw ISO timestamp — the add-field
    // options and the PDF format it the same way (the calendar date when the list carries it, else
    // the issue timestamp in Cairo time). `formatDate` only parses a 'YYYY-MM-DD' calendar date.
    const dateText = r.issueDate
      ? formatDate(r.issueDate)
      : r.dateTimeIssued
        ? formatDateTime(r.dateTimeIssued)
        : null;
    rows.push([
      r.number ?? '—',
      <span data-testid="review-reference">
        {r.totalAmount ? <Money value={r.totalAmount} currency={r.currency} /> : '—'}
        {r.remaining ? (
          <span className={styles.reviewSub}>
            {t.editor.note.stillReducible}: <Money value={r.remaining} currency="EGP" />
          </span>
        ) : null}
        {dateText ? <span className={styles.reviewSub}>{dateText}</span> : null}
        {r.etaId ? <span className={`${styles.reviewSub} ${styles.mono}`}>{r.etaId}</span> : null}
      </span>,
    ]);
  }
  return <Pairs rows={rows} />;
}

const REFERENCES = [
  ['purchaseOrderRef', 'poRef'],
  ['purchaseOrderDescription', 'poDesc'],
  ['salesOrderRef', 'soRef'],
  ['salesOrderDescription', 'soDesc'],
  ['proformaInvoiceNumber', 'proforma'],
] as const;

/** «تفاصيل المستند»: dates, currency and rate, the issuer with its branch and activity, references. */
export function ReviewDetailsSection({
  draft,
  organization,
  branch,
  activity,
}: {
  draft: Draft;
  organization: Organization | undefined;
  branch: Branch | undefined;
  activity: OrgActivityCode | undefined;
}) {
  const { t, formatDate, pick } = useI18n();
  const date = (value: string | undefined) => (value ? formatDate(value) : '—');
  const rows: [string, ReactNode][] = [
    [
      t.editor.settings.issueDate,
      draft.issueDate ? (
        formatDate(draft.issueDate)
      ) : (
        <>
          <span>—</span>
          <span className={styles.reviewSub}>{t.editor.settings.issueDateHint}</span>
        </>
      ),
    ],
    [t.editor.settings.dueDate, date(draft.dueDate)],
    [t.editor.settings.serviceDate, date(draft.serviceDeliveryDate)],
    [t.editor.settings.currency, <span className={styles.mono}>{draft.currency}</span>],
  ];
  if (draft.currency !== 'EGP')
    rows.push([
      t.editor.settings.exchangeRate,
      <span className={styles.mono}>{draft.exchangeRate || '—'}</span>,
    ]);
  rows.push([
    t.editor.sections.issuer,
    <>
      <span className={styles.reviewStrong}>
        {organization ? <UserText>{organization.legalName ?? organization.name}</UserText> : '—'}
      </span>
      {[organization?.rin, organization?.email, organization?.phone]
        .filter((v): v is string => Boolean(v))
        .map((v) => (
          <span key={v} className={`${styles.reviewSub} ${styles.mono}`}>
            {v}
          </span>
        ))}
    </>,
  ]);
  rows.push([
    t.editor.settings.branch,
    branch ? (
      <>
        <span>
          <UserText>{branch.name}</UserText>
          {` (${branch.eta_branch_id})`}
        </span>
        <UserText className={styles.reviewSub}>
          {formatAddress({
            street: branch.street,
            buildingNumber: branch.building_number,
            regionCity: branch.region_city,
            governate: branch.governate,
            country: branch.country,
          })}
        </UserText>
      </>
    ) : (
      '—'
    ),
  ]);
  rows.push([
    t.editor.settings.activityCode,
    activity ? (
      <>
        {`${activity.code} — `}
        <UserText>{pick(activity.nameAr, activity.nameEn)}</UserText>
      </>
    ) : (
      (draft.activityCode ?? '—')
    ),
  ]);
  for (const [key, label] of REFERENCES) {
    const value = draft[key];
    if (value) rows.push([t.editor.additional[label], <UserText>{value}</UserText>]);
  }
  return <Pairs rows={rows} />;
}

/** «بنود الفاتورة»: the lines as a read-only 8-column table, then the document-level discount. */
export function ReviewLinesSection({
  draft,
  items,
  taxes,
  result,
  noteLines,
}: {
  draft: Draft;
  items: Record<string, LineInfo>;
  taxes: OrganizationTax[];
  result: PreviewResult | undefined;
  /**
   * Block 11 (role-4 M-2): a note line's locked item, unit and taxes, by its referenced line id.
   * A note line carries no `itemId` or `taxIds` of its own, so without this its item and unit read
   * «—» and its taxes «بدون ضريبة»; here they render like step 3 and the PDF.
   */
  noteLines?: Map<string, ReferenceLineInfo>;
}) {
  const { t, pick } = useI18n();
  const headers = [
    '#',
    t.editor.lines.item,
    t.catalogue.unit,
    t.editor.lines.quantity,
    t.editor.lines.unitPrice(draft.currency),
    t.editor.lines.discount,
    t.editor.lines.taxes,
    t.editor.lines.total,
  ] as const;
  const END = new Set([3, 4, 5, 7]);
  const cell = (i: number, content: ReactNode) => (
    <td key={i} className={END.has(i) ? styles.end : undefined}>
      {/* display:none beside a real header; read as the label on phones (as in DataTable) */}
      <span className={styles.cellLabel}>{headers[i]}</span>
      <span>{content}</span>
    </td>
  );
  return (
    <>
      {draft.lines.length === 0 ? (
        <p className={styles.note}>{t.editor.lines.empty}</p>
      ) : (
        <div className={styles.scrollX}>
          <table className={`${styles.lineTable} ${styles.reviewLines}`}>
            <caption className="visually-hidden">{t.editor.sections.lines}</caption>
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} scope="col" className={END.has(i) ? styles.end : undefined}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {draft.lines.map((line, i) => {
                const info = line.itemId ? items[line.itemId] : undefined;
                const computed = result?.lines[i];
                const taxIds = line.taxIds ?? [];
                // Block 11 (M-2): a note line's locked facts, from the referenced invoice line. Its
                // item, selling unit and taxes come from here, not from the catalogue `info` (null
                // for a note line) or the draft's `taxIds` (empty for a note line).
                const refLine = line.referencedLineId
                  ? noteLines?.get(line.referencedLineId)?.line
                  : undefined;
                const name = refLine?.description ?? info?.name ?? '—';
                const code = refLine
                  ? `${refLine.itemType} ${refLine.itemCode}`.trim()
                  : info?.code;
                const unit = refLine?.unitType ?? info?.unit ?? '—';
                // RI-05: the price the engine used when the line carries none — the item's catalogue
                // price, or the unit value the engine reported for the line. `amountEGP` is the
                // per-line unit value in EGP; the engine requires an explicit price in any other
                // document currency (CT-B1), so the fallback applies to EGP documents only.
                const knownPrice =
                  info?.price && info.priceCurrency === draft.currency ? info.price : undefined;
                const effectivePrice =
                  line.unitPrice ??
                  knownPrice ??
                  (draft.currency === 'EGP' ? computed?.amountEGP : undefined);
                return (
                  <tr key={i}>
                    {cell(0, i + 1)}
                    {cell(
                      1,
                      <>
                        <span className={styles.reviewStrong}>{name}</span>
                        {code ? (
                          <span className={`${styles.reviewSub} ${styles.mono}`}>{code}</span>
                        ) : null}
                      </>,
                    )}
                    {cell(2, <span className={styles.mono}>{unit}</span>)}
                    {cell(3, <span className={styles.mono}>{line.quantity || '—'}</span>)}
                    {cell(
                      4,
                      <>
                        <Money value={effectivePrice ?? null} currency={draft.currency} />
                        {!line.unitPrice && effectivePrice ? (
                          <span className={styles.reviewSub}>
                            {' '}
                            · {t.editor.lines.fromCatalogue}
                          </span>
                        ) : null}
                      </>,
                    )}
                    {cell(5, <Money value={computed?.discountAmount ?? null} currency="EGP" />)}
                    {cell(
                      6,
                      // M-2: a note line's taxes are the referenced line's locked taxes (D-9), with
                      // the amount the engine computed for this note line; an invoice line uses its
                      // own `taxIds`. Both read consistently in every language and at every width.
                      refLine
                        ? refLine.taxes.length === 0
                          ? t.editor.lines.noTaxes
                          : refLine.taxes.map((tax, j) => {
                              const orgTax = tax.organizationTaxId
                                ? taxes.find((x) => x.id === tax.organizationTaxId)
                                : undefined;
                              const amount = computed?.taxes[j]?.amount;
                              return (
                                <span
                                  key={
                                    tax.organizationTaxId ?? `${tax.taxType}/${tax.subType}/${j}`
                                  }
                                  className={styles.reviewTax}
                                >
                                  {orgTax
                                    ? `${pick(orgTax.nameAr, orgTax.nameEn)} (${orgTax.taxType}/${orgTax.taxSubtype})`
                                    : `${tax.taxType}/${tax.subType}`}
                                  {amount ? (
                                    <>
                                      {' = '}
                                      <Money value={amount} currency="EGP" />
                                    </>
                                  ) : null}
                                </span>
                              );
                            })
                        : taxIds.length === 0
                          ? t.editor.lines.noTaxes
                          : taxIds.map((id, j) => {
                              const tax = taxes.find((x) => x.id === id);
                              const amount = computed?.taxes[j]?.amount;
                              return (
                                <span key={id} className={styles.reviewTax}>
                                  {tax
                                    ? `${pick(tax.nameAr, tax.nameEn)} (${tax.taxType}/${tax.taxSubtype})`
                                    : id}
                                  {amount ? (
                                    <>
                                      {' = '}
                                      <Money value={amount} currency="EGP" />
                                    </>
                                  ) : null}
                                </span>
                              );
                            }),
                    )}
                    {cell(7, <Money value={computed?.total ?? null} currency="EGP" />)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pairs
        rows={[
          [
            t.editor.totals.extraDiscount,
            <Money value={draft.extraDiscountAmount || '0'} currency="EGP" />,
          ],
        ]}
      />
    </>
  );
}
